/* ============================================================================
   ModalRelatorios / index.jsx
   Relatórios individuais do aluno — portal do responsável.
   Um card COLAPSÁVEL por período (bimestre/trimestre); clicar expande o texto.
   ========================================================================== */

   import React, { useState, useEffect, useRef } from 'react'
   import { IoClose } from 'react-icons/io5'
   import DOMPurify from 'dompurify'
   import { getIndividualFormsByStudentAndYear, fetchLogo, getSchoolYear } from '../../Api'
   import {
       Overlay, ModalCard, Body, Loading, Empty,
       ContLogo, /*Preview,*/ YearBar, InfoBox,
       FormBlock, FormHeader, FormHeaderLeft, FormPeriod, FormMeta, Chevron,
       FormBody, FormRich, FormPreview, FormPeople, TopBar, CloseButton
   } from './styles'
   
   /* ---------- helpers --------------------------------------------------------- */
   
   const QUARTER_KEYS = [
       'id_iStQuarter',
       'id_iiNdQuarter',
       'id_iiiRdQuarter',
       'id_ivThQuarter',
       'id_vThQuarter',
       'id_viThQuarter',
   ]
   
   // qual quarter da ficha está preenchido → índice do período (0-based)
   const periodIndexOf = (form) => {
       for (let i = 0; i < QUARTER_KEYS.length; i++) {
           if (form?.[QUARTER_KEYS[i]]) return i
       }
       return 99
   }
   
   // o objeto do quarter preenchido (para ler assessmentRegime)
   const quarterOf = (form) => {
       for (const k of QUARTER_KEYS) {
           const q = form?.[k]
           if (q && typeof q === 'object') return q
       }
       return null
   }
   
   const fmtDate = (d) => {
       if (!d) return ''
       const dt = new Date(d)
       if (isNaN(dt.getTime())) return ''
       return dt.toLocaleDateString('pt-BR')
   }
   
   // O relatório vem de um editor rich text (HTML: <p>, <strong>, <em>...).
   // Sanitiza antes de renderizar — nunca injetar HTML cru vindo do banco.
   const sanitizeHtml = (html) => DOMPurify.sanitize(String(html || ''), {
       ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li',
           'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'span', 'div'],
       ALLOWED_ATTR: [],
   })
   
   // versão em TEXTO PURO (para a prévia de 3 linhas quando colapsado).
   // Converte blocos em quebras antes de remover as tags, senão as frases colam.
   const htmlToText = (html) => {
       if (!html) return ''
       const withBreaks = String(html)
           .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, ' \n')
           .replace(/<br\s*\/?>/gi, ' \n')
       const clean = DOMPurify.sanitize(withBreaks, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
       return clean
           .replace(/&nbsp;/g, ' ')
           .replace(/[ \t]+/g, ' ')
           .replace(/\n{2,}/g, '\n')
           .trim()
   }
   
   // organiza as fichas por período e descobre o regime
   const buildForms = (forms) => {
       const list = Array.isArray(forms) ? forms : []
   
       // regime: lido do quarter da ficha do 1º período; se não houver,
       // procura em qualquer ficha. Sem informação, assume BIMESTRAL.
       let firstForm = null
       let firstIdx = Infinity
       for (const f of list) {
           const idx = periodIndexOf(f)
           if (idx < firstIdx) { firstIdx = idx; firstForm = f }
       }
       let regime = String(quarterOf(firstForm)?.assessmentRegime || '').trim().toUpperCase()
       if (!regime) {
           for (const f of list) {
               const r = quarterOf(f)?.assessmentRegime
               if (r) { regime = String(r).trim().toUpperCase(); break }
           }
       }
       const isTrimestral = regime === 'TRIMESTRAL'
       const periodLabel = isTrimestral ? 'Trimestre' : 'Bimestre'
   
       const items = list
           .map((f) => ({
               id: f?._id,
               idx: periodIndexOf(f),
               description: (f?.description || '').trim(),
               teacher: (f?.id_teacher?.name || '').trim(),
               teacher02: (f?.id_teacher02?.name || '').trim(),
               className: (f?.id_class?.name || '').trim(),
               serie: (f?.id_class?.serie || '').trim(),
               createdAt: f?.createdAt,
               updatedAt: f?.updatedAt,
           }))
           .sort((a, b) => {
               if (a.idx !== b.idx) return a.idx - b.idx
               return new Date(a.createdAt) - new Date(b.createdAt)
           })
   
       return { items, periodLabel }
   }
   
   /* ---------- componente ------------------------------------------------------ */
   
   const IndividualReportsModal = ({
       open,
       onClose,
       studentId,
       studentName,
       schoolName,
       idSchool,
       logoUrl: logoUrlProp,
   }) => {
       const [year, setYear] = useState(new Date().getFullYear())
       // ano letivo atual da escola — serve de teto para o seletor de anos
       const [anoLetivoAtual, setAnoLetivoAtual] = useState(new Date().getFullYear())
       const [forms, setForms] = useState([])
       const [loading, setLoading] = useState(false)
       const [, setLogoUrl] = useState(logoUrlProp || '')
       const [openIds, setOpenIds] = useState(() => new Set())
       const cancelled = useRef(false)
   
       const resolveSchoolId = () => {
           if (idSchool) return idSchool
           try {
               const s = JSON.parse(sessionStorage.getItem('id-school'))
               if (s) return s
           } catch (e) { /* ignora */ }
           return null
       }
       const schoolId = resolveSchoolId()
   
       // ano letivo da escola (fallback: ano atual)
       useEffect(() => {
           if (!open) return
           let active = true
               ; (async () => {
                   if (!schoolId) { if (active) setYear(new Date().getFullYear()); return }
                   try {
                       const res = await getSchoolYear(schoolId)
                       if (!active) return
                       const y = parseInt(res?.data?.data, 10)
                       const anoValido = !isNaN(y) ? y : new Date().getFullYear()
                       setYear(anoValido)
                       setAnoLetivoAtual(anoValido) // teto do seletor
                   } catch (e) {
                       if (active) {
                           setYear(new Date().getFullYear())
                           setAnoLetivoAtual(new Date().getFullYear())
                       }
                   }
               })()
           return () => { active = false }
       }, [open, schoolId])
   
       // busca as fichas individuais do ano
       useEffect(() => {
           if (!open || !studentId) return
           cancelled.current = false
           setLoading(true)
           const load = async () => {
               try {
                   const res = await getIndividualFormsByStudentAndYear(studentId, year)
                   if (cancelled.current) return
                   setForms(res?.data?.data || [])
                   setOpenIds(new Set())   // começa tudo colapsado
               } catch (err) {
                   if (cancelled.current) return
                   setForms([])
               } finally {
                   if (!cancelled.current) setLoading(false)
               }
           }
           load()
           return () => { cancelled.current = true }
       }, [open, year, studentId])
   
       // logo da escola (opcional)
       useEffect(() => {
           if (!open) return
           if (logoUrlProp) { setLogoUrl(logoUrlProp); return }
           if (!schoolId) return
   
           const cached = localStorage.getItem(`school-logo-${schoolId}`)
           if (cached) { setLogoUrl(cached); return }
   
           let active = true
               ; (async () => {
                   try {
                       const logoRes = await fetchLogo(schoolId)
                       if (active && logoRes?.url) {
                           setLogoUrl(logoRes.url)
                           localStorage.setItem(`school-logo-${schoolId}`, logoRes.url)
                           if (logoRes._id) localStorage.setItem(`school-logo-id-${schoolId}`, logoRes._id)
                       }
                   } catch (e) { /* logo é opcional */ }
               })()
           return () => { active = false }
       }, [open, logoUrlProp, schoolId])
   
       if (!open) return null
   
       const toggle = (id) => {
           setOpenIds((prev) => {
               const next = new Set(prev)
               if (next.has(id)) next.delete(id)
               else next.add(id)
               return next
           })
       }
   
       const { items, periodLabel } = buildForms(forms)
       const hasData = items.length > 0
   
       return (
           <Overlay onClick={onClose}>
               <ModalCard onClick={(e) => e.stopPropagation()}>
                   <TopBar>
                       {/* <PrintButton onClick={() => window.print()}>Imprimir</PrintButton>*/}
                       <CloseButton onClick={onClose} aria-label="Fechar">
                           <IoClose size={20} />
                       </CloseButton>
                   </TopBar>
                   <Body>
                       {/* Seletor de ano — SEMPRE visível (fora do bloco de dados),
                           para permitir trocar de ano mesmo quando o ano atual está vazio */}
                       <YearBar>
                           <label style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
                               <span style={{ fontWeight: 700 }}>Ano Letivo:</span>
                               <select
                                   value={year}
                                   onChange={(e) => setYear(Number(e.target.value))}
                                   style={{
                                       padding: '7px 12px',
                                       borderRadius: 8,
                                       border: '1.5px solid var(--line)',
                                       fontSize: 15,
                                       fontWeight: 600,
                                       fontFamily: 'inherit',
                                       color: 'var(--teal-text, var(--teal))',
                                       background: 'var(--surface)',
                                       cursor: 'pointer',
                                       outline: 'none'
                                   }}
                               >
                                   {Array.from({ length: 6 }, (_, i) => anoLetivoAtual - i).map((y) => (
                                       <option key={y} value={y}>
                                           {y}{y === anoLetivoAtual ? ' (atual)' : ''}
                                       </option>
                                   ))}
                               </select>
                           </label>
                       </YearBar>
   
                       {loading ? (
                           <Loading>Carregando relatórios…</Loading>
                       ) : !hasData ? (
                           <Empty>Ainda não há relatórios lançados para {year}.</Empty>
                       ) : (
                           <>
                               <ContLogo>
                                   {/*logoUrl && <Preview src={logoUrl} alt="Logo da escola" />*/}
                                   <h2>Relatório Individual</h2>
                               </ContLogo>
   
                               <InfoBox>
                                   <span><strong>Escola:</strong> {schoolName || '—'}</span>
                                   <span><strong>Aluno(a):</strong> {studentName || '—'}</span>
                               </InfoBox>
   
                               {items.map((f) => {
                                   const isOpen = openIds.has(f.id)
                                   const periodo = f.idx <= 5
                                       ? `${f.idx + 1}º ${periodLabel}`
                                       : 'Período não informado'
                                   const preview = htmlToText(f.description)
                                   return (
                                       <FormBlock key={f.id}>
                                           <FormHeader
                                               type="button"
                                               $open={isOpen}
                                               onClick={() => toggle(f.id)}
                                               aria-expanded={isOpen}
                                           >
                                               <FormHeaderLeft>
                                                   <FormPeriod>{periodo}</FormPeriod>
   
                                                   {/* professores e turma — sempre no topo */}
                                                   <FormPeople $open={isOpen}>
                                                       {f.teacher && (
                                                           <span><strong>Professor(a) Titular:</strong>{f.teacher}</span>
                                                       )}
                                                       {f.teacher02 && (
                                                           <span><strong>Professor(a) Adjunto:</strong>{f.teacher02}</span>
                                                       )}
                                                       {f.className && (
                                                           <span><strong>Turma:</strong>{f.className}</span>
                                                       )}
                                                       {console.log("fff", f)}
                                                       {f.serie && (
                                                           <span><strong>Serie:</strong>{f.serie}</span>
                                                       )}
                                                       {fmtDate(f.updatedAt || f.createdAt) && (
                                                           <span><strong>Atualizado em:</strong>{fmtDate(f.updatedAt || f.createdAt)}</span>
                                                       )}
                                                   </FormPeople>
   
                                                   {/* prévia de 3 linhas — só quando colapsado */}
                                                   {!isOpen && preview && (
                                                       <FormPreview>{preview}</FormPreview>
                                                   )}
   
                                                   <FormMeta>
                                                       {isOpen ? 'Clique para recolher' : 'Clique para ler o relatório completo'}
                                                   </FormMeta>
                                               </FormHeaderLeft>
                                               <Chevron $open={isOpen}>›</Chevron>
                                           </FormHeader>
   
                                           {isOpen && (
                                               <FormBody>
                                                   {/* o relatório vem em HTML (editor rich text) — sanitizado acima */}
                                                   {f.description ? (
                                                       <FormRich
                                                           dangerouslySetInnerHTML={{
                                                               __html: sanitizeHtml(f.description),
                                                           }}
                                                       />
                                                   ) : (
                                                       <FormRich>Relatório sem descrição.</FormRich>
                                                   )}
                                               </FormBody>
                                           )}
                                       </FormBlock>
                                   )
                               })}
                           </>
                       )}
                   </Body>
               </ModalCard>
           </Overlay>
       )
   }
   
   export default IndividualReportsModal